/*
  /assets/vehicle-finder.js

  <vehicle-finder> custom element for the Broadcast "Vehicle finder" section.

  Data source is GoVedia / PartFinder (https://car.govedia.com). The app
  identifies the shop by the request Referer, so plain fetch() from the
  storefront works with no key. Vehicle lists are derived from the product
  tags GoVedia writes, so nothing changes in how vehicles are managed.

  Results URL matches the legacy theme exactly:
    /collections/<handle>/<Make-Model-Year tag>
  (tag-path filtering; ?filter.p.tag is not enabled on this store)

  Cookies are written in GoVedia's own format so any GoVedia widget on the
  site (or a future one) agrees with this component on the remembered car:
    gv_mmy          {customer, mmy: "<raw tag>"}
    gv_vehicle_json {customer, mmy: {make, model, submodel, year}}
*/
(function () {
  if (window.customElements.get('vehicle-finder')) return;

  const COOKIE_TAG = 'gv_mmy';
  const COOKIE_VEHICLE = 'gv_vehicle_json';
  const COOKIE_DAYS = 30;

  const replaceAll = (str, find, rep) => str.split(find).join(rep);

  // Mirrors GoVedia's EncodeUrl / EncodeTag so tags match what the app wrote.
  const encodeRoute = (v) => encodeURIComponent(replaceAll(String(v), '/', '-fs-'));
  const encodeTag = (tag) => {
    tag = replaceAll(tag, '-fs-', '-');
    tag = replaceAll(tag, ' ', '-');
    tag = replaceAll(tag, '&', '-');
    tag = replaceAll(tag, '.', '-');
    tag = replaceAll(tag, ',', '-');
    tag = replaceAll(tag, '--', '-');
    tag = replaceAll(tag, '"', '');
    return tag;
  };

  const readCookie = (name) => {
    const key = encodeURIComponent(name) + '=';
    const parts = document.cookie.split(';');
    for (let c of parts) {
      c = c.trim();
      if (c.indexOf(key) === 0) {
        try {
          return JSON.parse(decodeURIComponent(c.substring(key.length)));
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  };

  const uid = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });

  const writeCookie = (name, value, days) => {
    const existing = readCookie(name) || { customer: '' };
    const customer =
      (window.MMY_Customer && String(window.MMY_Customer)) || existing.customer || uid();
    let expires = '';
    if (days) {
      const d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + d.toUTCString();
    }
    document.cookie =
      encodeURIComponent(name) +
      '=' +
      JSON.stringify({ customer, mmy: value }) +
      expires +
      '; path=/; SameSite=Lax';
  };

  const eraseCookies = () => {
    writeCookie(COOKIE_TAG, '', -1);
    writeCookie(COOKIE_VEHICLE, '', -1);
  };

  class VehicleFinder extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onReset = this.onReset.bind(this);
      this.onEdit = this.onEdit.bind(this);
    }

    connectedCallback() {
      this.api = (this.dataset.api || 'https://car.govedia.com').replace(/\/+$/, '');
      this.resultsBase = (this.dataset.resultsBase || '/collections/all').replace(/\/+$/, '');
      this.sortYears = this.dataset.sortYears || 'desc';
      this.enableFallback = this.dataset.fallback === 'true';
      this.rememberVehicle = this.dataset.remember !== 'false';

      this.form = this.querySelector('[data-vf-form]');
      this.yr = this.querySelector('[data-vf-year]');
      this.mk = this.querySelector('[data-vf-make]');
      this.md = this.querySelector('[data-vf-model]');
      this.btnSearch = this.querySelector('[data-vf-search]');
      this.btnReset = this.querySelector('[data-vf-reset]');
      this.summary = this.querySelector('[data-vf-summary]');
      this.summaryText = this.querySelector('[data-vf-summary-text]');
      this.summaryShop = this.querySelector('[data-vf-summary-shop]');
      this.btnEdit = this.querySelector('[data-vf-edit]');
      this.status = this.querySelector('[data-vf-status]');
      this.fallback = this.querySelector('[data-vf-fallback]');

      if (!this.form || !this.yr || !this.mk || !this.md) return;

      this.prompts = {
        year: this.yr.dataset.prompt || 'Year',
        make: this.mk.dataset.prompt || 'Make',
        model: this.md.dataset.prompt || 'Model',
      };

      this.yr.addEventListener('change', this.onChange);
      this.mk.addEventListener('change', this.onChange);
      this.md.addEventListener('change', this.onChange);
      this.form.addEventListener('submit', this.onSubmit);
      if (this.btnReset) this.btnReset.addEventListener('click', this.onReset);
      if (this.btnEdit) this.btnEdit.addEventListener('click', this.onEdit);

      this.classList.add('is-ready');
      this.init();
    }

    disconnectedCallback() {
      this.yr && this.yr.removeEventListener('change', this.onChange);
      this.mk && this.mk.removeEventListener('change', this.onChange);
      this.md && this.md.removeEventListener('change', this.onChange);
      this.form && this.form.removeEventListener('submit', this.onSubmit);
      this.btnReset && this.btnReset.removeEventListener('click', this.onReset);
      this.btnEdit && this.btnEdit.removeEventListener('click', this.onEdit);
    }

    /* ---------- data ---------- */

    async get(path) {
      const res = await fetch(this.api + path, {
        method: 'GET',
        credentials: 'omit',
        referrerPolicy: 'strict-origin-when-cross-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('GoVedia ' + res.status + ' for ' + path);
      return res.json();
    }

    async loadYears() {
      const data = await this.get('/ShopifyYMM/years');
      let years = Array.isArray(data) ? data : data.years || [];
      years = years.filter((y) => String(y) !== '9999');
      if (this.sortYears === 'asc') years = years.slice().sort((a, b) => a - b);
      else years = years.slice().sort((a, b) => b - a);
      return years;
    }

    loadMakes(year) {
      return this.get('/ShopifyYMM/years/' + encodeRoute(year) + '/makes').then(
        (d) => (Array.isArray(d) ? d : d.makes || [])
      );
    }

    loadModels(year, make) {
      return this.get(
        '/ShopifyYMM/years/' + encodeRoute(year) + '/makes/' + encodeRoute(make) + '/models'
      ).then((d) => (Array.isArray(d) ? d : d.models || []));
    }

    /* ---------- ui ---------- */

    fill(select, items, prompt, selected) {
      select.innerHTML = '';
      const first = document.createElement('option');
      first.value = '';
      first.textContent = prompt;
      select.appendChild(first);
      items.forEach((item) => {
        const opt = document.createElement('option');
        opt.value = String(item);
        opt.textContent = String(item).replace(/\s{2,}/g, ' ');
        if (selected !== undefined && String(item) === String(selected)) opt.selected = true;
        select.appendChild(opt);
      });
      select.disabled = items.length === 0;
    }

    clear(select, prompt) {
      this.fill(select, [], prompt);
    }

    setBusy(el, busy) {
      if (!el) return;
      el.classList.toggle('is-busy', !!busy);
      if (busy) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');
    }

    setStatus(msg, isError) {
      if (!this.status) return;
      this.status.textContent = msg || '';
      this.status.hidden = !msg;
      this.status.classList.toggle('is-error', !!isError);
    }

    updateButtons() {
      const ready = this.yr.value && this.mk.value && this.md.value;
      if (this.btnSearch) this.btnSearch.disabled = !ready;
      if (this.btnReset) this.btnReset.hidden = !(this.yr.value || this.mk.value || this.md.value);
    }

    vehicleLabel(v) {
      return [v.year, v.make, v.model]
        .filter(Boolean)
        .join(' ')
        .replace(/\s{2,}/g, ' ');
    }

    showSummary(vehicle) {
      if (!this.summary) return;
      const label = this.vehicleLabel(vehicle);
      if (this.summaryText) this.summaryText.textContent = label;
      if (this.summaryShop) this.summaryShop.href = this.resultsUrl(vehicle);
      this.summary.hidden = false;
      this.form.hidden = true;
      this.classList.add('has-vehicle');
    }

    hideSummary() {
      if (!this.summary) return;
      this.summary.hidden = true;
      this.form.hidden = false;
      this.classList.remove('has-vehicle');
    }

    /* ---------- flow ---------- */

    async init() {
      this.setBusy(this, true);
      this.clear(this.mk, this.prompts.make);
      this.clear(this.md, this.prompts.model);
      this.updateButtons();

      const remembered = this.rememberVehicle ? (readCookie(COOKIE_VEHICLE) || {}).mmy : null;
      const hasRemembered =
        remembered && typeof remembered === 'object' && remembered.year && remembered.make && remembered.model;

      try {
        const years = await this.loadYears();
        // GoVedia keys the shop off the referring domain. An empty list means
        // it did not recognise this host (e.g. a *.shopifypreview.com share
        // link), not that there are no vehicles. Say so instead of leaving
        // three disabled dropdowns.
        if (!years.length) {
          this.setStatus(this.dataset.emptyText || this.dataset.errorText || 'No vehicles available.', true);
          this.updateButtons();
          return;
        }
        this.fill(this.yr, years, this.prompts.year, hasRemembered ? remembered.year : undefined);

        if (hasRemembered && this.yr.value) {
          const makes = await this.loadMakes(this.yr.value);
          this.fill(this.mk, makes, this.prompts.make, remembered.make);
          if (this.mk.value) {
            const models = await this.loadModels(this.yr.value, this.mk.value);
            this.fill(this.md, models, this.prompts.model, remembered.model);
          }
          if (this.yr.value && this.mk.value && this.md.value) {
            this.showSummary({ year: this.yr.value, make: this.mk.value, model: this.md.value });
          }
        }
        this.updateButtons();
      } catch (err) {
        console.warn('[vehicle-finder]', err);
        this.fail();
      } finally {
        this.setBusy(this, false);
      }
    }

    async onChange(e) {
      const which = e.target;
      this.setStatus('');
      try {
        if (which === this.yr) {
          this.clear(this.mk, this.prompts.make);
          this.clear(this.md, this.prompts.model);
          this.updateButtons();
          if (!this.yr.value) return;
          this.setBusy(this.mk, true);
          const makes = await this.loadMakes(this.yr.value);
          this.fill(this.mk, makes, this.prompts.make);
          // Single make for that year? Pick it and move on.
          if (makes.length === 1) {
            this.mk.value = makes[0];
            await this.onChange({ target: this.mk });
          }
        } else if (which === this.mk) {
          this.clear(this.md, this.prompts.model);
          this.updateButtons();
          if (!this.mk.value) return;
          this.setBusy(this.md, true);
          const models = await this.loadModels(this.yr.value, this.mk.value);
          this.fill(this.md, models, this.prompts.model);
        }
        this.updateButtons();
      } catch (err) {
        console.warn('[vehicle-finder]', err);
        this.setStatus(this.dataset.errorText || 'We could not load vehicles right now. Please try again.', true);
      } finally {
        this.setBusy(this.mk, false);
        this.setBusy(this.md, false);
      }
    }

    resultsUrl(vehicle) {
      const rawTag = [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join('-');
      return this.resultsBase + '/' + encodeTag(rawTag);
    }

    onSubmit(e) {
      e.preventDefault();
      const vehicle = {
        year: this.yr.value,
        make: this.mk.value,
        model: this.md.value,
        submodel: '',
      };
      if (!vehicle.year || !vehicle.make || !vehicle.model) {
        this.updateButtons();
        return;
      }
      const rawTag = [vehicle.make, vehicle.model, vehicle.year].join('-');
      if (this.rememberVehicle) {
        writeCookie(COOKIE_TAG, rawTag, COOKIE_DAYS);
        writeCookie(COOKIE_VEHICLE, vehicle, COOKIE_DAYS);
      }
      if (this.btnSearch) this.btnSearch.classList.add('is-loading');
      window.location.href = this.resultsUrl(vehicle);
    }

    onReset(e) {
      e.preventDefault();
      eraseCookies();
      this.hideSummary();
      this.yr.value = '';
      this.clear(this.mk, this.prompts.make);
      this.clear(this.md, this.prompts.model);
      this.setStatus('');
      this.updateButtons();
      this.yr.focus();
    }

    onEdit(e) {
      e.preventDefault();
      this.hideSummary();
      this.updateButtons();
      this.yr.focus();
    }

    // If the GoVedia API is unreachable, hand the slot to GoVedia's own
    // storewide script (it watches the DOM for #gv-mmy) so the page still
    // works, just without our UI.
    fail() {
      if (this.enableFallback && this.fallback) {
        this.form.hidden = true;
        if (this.summary) this.summary.hidden = true;
        this.fallback.hidden = false;
        if (!this.fallback.querySelector('#gv-mmy')) {
          const slot = document.createElement('div');
          slot.id = 'gv-mmy';
          slot.className = 'clearfix';
          this.fallback.appendChild(slot);
          if (window.MMY && typeof window.MMY.initAll === 'function') {
            try {
              window.MMY.initAll();
            } catch (err) {
              /* GoVedia will pick it up via its observer */
            }
          }
        }
        this.classList.add('is-fallback');
      } else {
        this.setStatus(this.dataset.errorText || 'We could not load vehicles right now. Please try again.', true);
      }
    }
  }

  window.customElements.define('vehicle-finder', VehicleFinder);
})();
